export interface LocaleConfig {
  /** URL-prefix / i18next language code, e.g. "de" */
  code: string;
  /** Native display name, shown in the language switcher */
  label: string;
  /** Value for the <html lang> attribute (Chinese needs the region subtag) */
  htmlLang: string;
}

export const DEFAULT_LOCALE = 'en';

export const LOCALES: LocaleConfig[] = [
  { code: 'en', label: 'English', htmlLang: 'en' },
  { code: 'de', label: 'Deutsch', htmlLang: 'de' },
  { code: 'fr', label: 'Français', htmlLang: 'fr' },
  { code: 'es', label: 'Español', htmlLang: 'es' },
  { code: 'it', label: 'Italiano', htmlLang: 'it' },
  { code: 'zh', label: '简体中文', htmlLang: 'zh-CN' },
  { code: 'zh-tw', label: '繁體中文', htmlLang: 'zh-TW' },
  { code: 'ru', label: 'Русский', htmlLang: 'ru' },
  { code: 'ms', label: 'Bahasa Melayu', htmlLang: 'ms' },
  { code: 'id', label: 'Bahasa Indonesia', htmlLang: 'id' },
  { code: 'ja', label: '日本語', htmlLang: 'ja' },
  { code: 'ko', label: '한국어', htmlLang: 'ko' },
];

/**
 * IP-geolocation country code -> locale, used by middleware.ts for a
 * first-visit-only redirect. Deliberately not exhaustive - unmapped
 * countries fall back to English, which is always a safe default.
 */
export const COUNTRY_TO_LOCALE: Record<string, string> = {
  DE: 'de',
  AT: 'de',
  FR: 'fr',
  ES: 'es',
  MX: 'es',
  AR: 'es',
  CO: 'es',
  CL: 'es',
  PE: 'es',
  VE: 'es',
  EC: 'es',
  GT: 'es',
  CU: 'es',
  BO: 'es',
  DO: 'es',
  HN: 'es',
  PY: 'es',
  SV: 'es',
  NI: 'es',
  CR: 'es',
  PA: 'es',
  UY: 'es',
  IT: 'it',
  CN: 'zh',
  TW: 'zh-tw',
  HK: 'zh-tw',
  MO: 'zh-tw',
  RU: 'ru',
  MY: 'ms',
  BN: 'ms',
  ID: 'id',
  JP: 'ja',
  KR: 'ko',
};

export const NON_DEFAULT_LOCALE_CODES = LOCALES
  .filter((l) => l.code !== DEFAULT_LOCALE)
  .map((l) => l.code);

export const LOCALE_CODES = LOCALES.map((l) => l.code);

/**
 * Routes that are intentionally NOT translated (the blog: machine-translating
 * the personal, first-person posts would violate the site's writing rules).
 * Matches the path itself or any nested path below it.
 */
export const UNLOCALIZED_PATH_PREFIXES = ['/blog', '/destinations', '/privacy-policy', '/terms-of-service'];

export function isSupportedLocale(code: string | undefined): code is string {
  return !!code && LOCALE_CODES.includes(code);
}

export function getLocaleConfig(code: string): LocaleConfig {
  return LOCALES.find((l) => l.code === code) ?? LOCALES[0];
}

export function isUnlocalizedPath(path: string): boolean {
  return UNLOCALIZED_PATH_PREFIXES.some((prefix) => path === prefix || path.startsWith(`${prefix}/`));
}
